import React from 'react';
import { EMAIL_RE, isPhoneComplete } from '../../lib/utils';
import { PortalField } from '../FormField/PortalField';
import { PortalInput } from '../FormField/PortalInput';
import styles from './FosterContactFields.module.scss';

/** The four fields both foster-contact writes take. Held as one object so the form is one setState. */
export interface FosterContactDraft {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

export const EMPTY_FOSTER_CONTACT: FosterContactDraft = { firstName: '', lastName: '', email: '', phone: '' };

/** Empty passes, so the error only appears once there's something wrong to point at. */
export function isDraftEmailValid(draft: FosterContactDraft): boolean {
  const email = draft.email.trim();
  return !email || EMAIL_RE.test(email);
}

/** Empty passes, for the same reason {@link isDraftEmailValid} lets it. */
export function isDraftPhoneValid(draft: FosterContactDraft): boolean {
  const phone = draft.phone.trim();
  return !phone || isPhoneComplete(phone);
}

/**
 * Enough to submit. Both writes — `rescueCreateFosterHome` and `rescueCreatePetFosterTransfer` —
 * require a first name, an email and a phone, so neither form should accept what the other rejects
 * and leave the rescue to discover it from a server error.
 *
 * Stricter than the two field-level rules, which both accept empty: those drive the inline errors,
 * this drives the submit button. The `*` markers are what tie them together — without one, a blank
 * required field is a disabled button with nothing on screen explaining it.
 */
export function isDraftSubmittable(draft: FosterContactDraft): boolean {
  return (
    !!draft.firstName.trim() && !!draft.email.trim() && isDraftEmailValid(draft) && isPhoneComplete(draft.phone.trim())
  );
}

interface FosterContactFieldsProps {
  value: FosterContactDraft;
  onChange: (next: FosterContactDraft) => void;
  /**
   * Fired when email or phone loses focus — where both callers hang the "already on your roster"
   * lookup, since those are the two fields the backend's reuse matcher keys on.
   */
  onContactBlur?: () => void;
  disabled?: boolean;
}

/**
 * The name / email / phone block shared by the two places a rescue types a foster contact: adding
 * one to the roster ahead of a placement, and entering a new one during a placement. Both submit the
 * same fields under the same rules, so they render the same form.
 */
export default function FosterContactFields({ value, onChange, onContactBlur, disabled }: FosterContactFieldsProps) {
  const emailValid = isDraftEmailValid(value);
  // The `*` says a phone is needed; this says why a typed one still isn't enough. Empty passes, the
  // same way the email rule does, so a fresh form isn't scolded before it has been filled in.
  const phoneValid = isDraftPhoneValid(value);
  const set = (field: keyof FosterContactDraft) => (event: React.ChangeEvent<HTMLInputElement>) =>
    onChange({ ...value, [field]: event.target.value });

  return (
    <>
      <PortalField label="Foster name *">
        <div className={styles.fieldRow}>
          <PortalInput
            value={value.firstName}
            onChange={set('firstName')}
            placeholder="First name"
            disabled={disabled}
          />
          <PortalInput value={value.lastName} onChange={set('lastName')} placeholder="Last name" disabled={disabled} />
        </div>
      </PortalField>

      <PortalField label="Foster email *" error="Enter a valid email address." showError={!emailValid}>
        <PortalInput
          type="email"
          value={value.email}
          onChange={set('email')}
          onBlur={onContactBlur}
          placeholder="e.g. lisa@gmail.com"
          hasError={!emailValid}
          disabled={disabled}
        />
      </PortalField>

      <PortalField label="Foster phone *" error="Enter a 10-digit phone number." showError={!phoneValid}>
        <PortalInput
          type="tel"
          value={value.phone}
          onChange={set('phone')}
          onBlur={onContactBlur}
          placeholder="e.g. (555) 123-4567"
          hasError={!phoneValid}
          disabled={disabled}
        />
      </PortalField>
    </>
  );
}
